import { motion } from "framer-motion";
import { Settings2, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ChatMessages } from "./ChatMessages";
import { ChatInputSection } from "./ChatInputSection";
import { Message } from "@/types/chat";

interface ChatContainerProps {
  messages: Message[];
  userInput: string;
  isLoading: boolean;
  isAnalyzing: boolean;
  isGenerating: boolean;
  progress: number;
  canShowServices: boolean;
  onUserInputChange: (value: string) => void;
  onSendMessage: () => void;
  onCreateDocument: () => void;
  onCreateCase: () => void;
  onLawSearch: () => void;
  onFileUpload: () => void;
  onToggleConsent: () => void;
  showConsent: boolean;
}

export function ChatContainer({
  messages,
  userInput,
  isLoading,
  isAnalyzing,
  isGenerating,
  progress,
  canShowServices,
  onUserInputChange,
  onSendMessage,
  onCreateDocument,
  onCreateCase,
  onLawSearch,
  onFileUpload,
  onToggleConsent,
  showConsent
}: ChatContainerProps) {
  return (
    <motion.div 
      className={cn(
        "flex-1 flex flex-col min-h-0 overflow-hidden bg-card/50 backdrop-blur-sm",
        "rounded-none md:rounded-lg border-x md:border shadow-lg",
        "w-[95%] mx-auto md:w-auto"
      )}
      layout
    >
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-2 py-2 md:px-4 md:py-3 border-b">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              onClick={onToggleConsent}
            >
              <Settings2 className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 text-primary"
              onClick={() => window.open('/help', '_blank')}
            >
              <HelpCircle className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
      
      <div className="flex-1 min-h-0 overflow-y-auto scroll-smooth w-full px-[2.5%] md:px-4">
        <ChatMessages
          messages={messages}
          isLoading={isLoading}
        />
      </div>

      <div className="sticky bottom-0 w-full bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-t">
        <ChatInputSection
          userInput={userInput}
          onChange={onUserInputChange}
          onSend={onSendMessage}
          isLoading={isLoading}
          isAnalyzing={isAnalyzing}
          onFileUpload={onFileUpload}
          onCreateDocument={onCreateDocument}
          onCreateCase={onCreateCase}
          onLawSearch={onLawSearch}
          messagesExist={messages.length > 1}
          isGenerating={isGenerating}
          progress={progress}
          canGenerateDocument={canShowServices}
        />
      </div>
    </motion.div>
  );
}